import { useState, type ComponentProps } from 'react'

import type { ResultadoCotizacion as Resultado } from '../api'
import { anios, num, pct, usd } from '../formato'
import { Avisos, SistemaIndustrial, SupuestosIndustrial } from './DetalleIndustrial'
import { ConsumoYEspera } from './CostoEsperar'
import { GraficoFlujo } from './GraficoFlujo'
import { TablaFlujo, TablaLineas } from './TablasCotizacion'

function Indicador({ titulo, valor, detalle }: { titulo: string; valor: string; detalle?: string }) {
  return (
    <div className="indicador">
      <span className="indicador-titulo">{titulo}</span>
      <span className="indicador-valor">{valor}</span>
      {detalle && <span className="indicador-detalle">{detalle}</span>}
    </div>
  )
}

type EdicionCostos = ComponentProps<typeof TablaLineas>['costos']
type EdicionMargenes = ComponentProps<typeof TablaLineas>['margenes']

interface Props {
  resultado: Resultado
  alQuitar?: (codigo: string) => void
  costos?: EdicionCostos
  /** Con esto la tabla muestra la columna «Utilidad %», editable por rubro. */
  margenes?: EdicionMargenes
}

export function ResultadoCotizacion({ resultado, alQuitar, costos, margenes }: Props) {
  const [pestana, setPestana] = useState<'equipos' | 'flujo'>('equipos')
  const { dimensionado: d, totales: t, financiero: f } = resultado
  const industrial = resultado.tipo === 'industrial'
  const ahorroDetalle = industrial
    ? `energía ${usd(f.ahorro_energia_anual ?? 0)} · demanda ${usd(f.ahorro_demanda_anual ?? 0)}`
    : `${usd(f.ahorro_primer_anio / 12)} al mes`

  return (
    <div className="resultado">
      <div className={f.es_viable ? 'veredicto viable' : 'veredicto no-viable'}>
        <strong>{f.es_viable ? '✔ Conviene' : '✖ No conviene'}</strong>
        <span>
          {f.es_viable
            ? `El VAN es positivo: rinde más que dejar el dinero al ${pct(f.tasa_descuento, 2)}.`
            : `El VAN es negativo a una tasa del ${pct(f.tasa_descuento, 2)}. Revisa tarifa, descuento o tamaño.`}
        </span>
      </div>

      <div className="indicadores">
        <Indicador
          titulo="Total con IVA"
          valor={usd(t.total)}
          detalle={`${usd(t.base_imponible)} sin IVA${t.valor_descuento > 0 ? ` · descuento ${pct(t.descuento_pct)}` : ''}`}
        />
        <Indicador titulo="VAN" valor={usd(f.van)} detalle={`tasa ${pct(f.tasa_descuento, 2)}`} />
        <Indicador titulo="TIR" valor={pct(f.tir)} />
        <Indicador titulo="Recuperación" valor={anios(f.payback_anios)} detalle={`descontada: ${anios(f.payback_descontado_anios)}`} />
        <Indicador titulo="Ahorro año 1" valor={usd(f.ahorro_primer_anio)} detalle={ahorroDetalle} />
        <Indicador titulo="Utilidad SunSmart" valor={usd(t.utilidad)} detalle={`${pct(t.utilidad_pct)} sobre costo`} />
      </div>

      {industrial && <Avisos avisos={d.avisos} />}

      {industrial ? (
        <div className="columnas">
          <SistemaIndustrial resultado={resultado} />
          <SupuestosIndustrial resultado={resultado} />
        </div>
      ) : (
      <div className="columnas">
        <div className="tarjeta">
          <h2>Sistema</h2>
          <dl className="lista-datos">
            <dt>Potencia</dt>
            <dd>{num(d.potencia_kwp)} kWp</dd>
            <dt>Paneles</dt>
            <dd>
              {d.paneles} × {d.panel.nombre}
            </dd>
            <dt>Inversor</dt>
            <dd>
              {d.inversores} × {d.inversor.nombre}
            </dd>
            {d.bateria && (
              <>
                <dt>Batería</dt>
                <dd>
                  {d.baterias && d.baterias > 1 ? `${d.baterias} × ` : ''}
                  {d.bateria.nombre}
                </dd>
              </>
            )}
            {d.kit && (
              <>
                <dt>Kit</dt>
                <dd>{d.kit}</dd>
              </>
            )}
            <dt>Generación</dt>
            <dd>{num(d.generacion_anual_kwh)} kWh/año</dd>
            <dt>Consumo</dt>
            <dd>{num(d.consumo_kwh_mes)} kWh/mes</dd>
          </dl>
        </div>
        <div className="tarjeta">
          <h2>Supuestos del análisis</h2>
          <dl className="lista-datos">
            <dt>Tarifa</dt>
            <dd>
              ${f.tarifa_usd_kwh.toFixed(4)}/kWh{' '}
              <span className={`insignia ${f.tarifa_origen === 'manual' ? 'insignia-pendiente' : 'insignia-cliente'}`}>
                {f.tarifa_origen === 'manual' ? 'manual' : `pliego ${f.categoria.replace('_', ' ')}`}
              </span>
            </dd>
            <dt>LCOE</dt>
            <dd>
              ${f.lcoe.toFixed(4)}/kWh {f.lcoe_vence_a_la_tarifa ? '(más barato que la red)' : '(más caro que la red)'}
            </dd>
            <dt>Beneficio/costo</dt>
            <dd>{num(f.costo_beneficio)}</dd>
            <dt>ROI (25 años)</dt>
            <dd>{pct(f.roi, 0)}</dd>
            <dt>Mantenimiento</dt>
            <dd>{usd(f.opex_total)} en 25 años</dd>
            <dt>Reposición inversor</dt>
            <dd>{usd(f.reposicion_inversor_usd)}</dd>
            <dt>Irradiación</dt>
            <dd>{num(d.irradiacion_kwh_m2_anio)} kWh/m²·año</dd>
          </dl>
        </div>
      </div>
      )}

      <ConsumoYEspera resultado={resultado} />

      <div className="tarjeta">
        <h2>¿Cuándo se recupera la inversión?</h2>
        <GraficoFlujo anios={f.anios} inversion={t.base_imponible} payback={f.payback_anios} />
      </div>

      <div className="tarjeta">
        <div className="filtros">
          <button className={pestana === 'equipos' ? 'chip activo' : 'chip'} onClick={() => setPestana('equipos')}>
            Equipos y precios
          </button>
          <button className={pestana === 'flujo' ? 'chip activo' : 'chip'} onClick={() => setPestana('flujo')}>
            Flujo año a año
          </button>
        </div>
        {pestana === 'equipos' ? <TablaLineas lineas={resultado.lineas} totales={t} alQuitar={alQuitar} costos={costos} margenes={margenes} /> : <TablaFlujo anios={f.anios} />}
      </div>
    </div>
  )
}
